import { useState, useEffect, useCallback } from "react";

const FONT_URL = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap";

// ─── Persistent Storage Helpers ───
const STORAGE_KEYS = { users: "gx-users", exclusions: "gx-exclusions", assignments: "gx-assignments", settings: "gx-settings" };
const DEFAULT_SETTINGS = { testUsersEnabled: false, giftLimit: 50 };

const API_BASE = window.location.origin + "/api/storage";
const AUTH_ENDPOINT = window.location.origin + "/api/auth/login";
const CHANGE_PW_ENDPOINT = window.location.origin + "/api/auth/change-password";
const UPDATE_PROFILE_ENDPOINT = window.location.origin + "/api/auth/update-profile";

const getToken = () => sessionStorage.getItem("gx-token");
const setToken = t => sessionStorage.setItem("gx-token", t);
const clearToken = () => sessionStorage.removeItem("gx-token");
const authHeaders = () => {
  const t = getToken();
  return t
    ? { "Content-Type": "application/json", "Authorization": `Bearer ${t}` }
    : { "Content-Type": "application/json" };
};

const loadData = async (key, fallback) => {
  try {
    const r = await fetch(`${API_BASE}/${encodeURIComponent(key)}`, { headers: authHeaders() });
    if (r.status === 401) return fallback;
    const data = await r.json();
    return data.value ? JSON.parse(data.value) : fallback;
  } catch { return fallback; }
};
const saveData = async (key, val) => {
  try {
    await fetch(`${API_BASE}/${encodeURIComponent(key)}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify({ value: JSON.stringify(val) }),
    });
  } catch (e) { console.error(e); }
};

// ─── Default Admin ───
const DEFAULT_ADMIN = {
  id: "admin-001",
  userName: "admin",
  firstName: "Admin",
  dateOfBirth: "1990-01-01",
  email: "admin@giftexchange.com",
  wishlistUrl: "",
  isAdmin: true,
};

// ─── Dummy Test Users ───
// Family A: Emma (parent) + Liam, Olivia (children)
// Family B: Noah (parent) + Sophia (child)
// Family C: Mason (parent) + Ava, Ethan (children)
const DUMMY_USERS = [
  // Family A — Emma's household
  { id: "user-t01", userName: "emma.w",   firstName: "Emma",   dateOfBirth: "1985-03-14", email: "emma@example.com",   wishlistUrl: "https://amazon.com/hz/wishlist/emma",   isAdmin: false, isParent: true },
  { id: "user-t02", userName: "liam.w",   firstName: "Liam",   dateOfBirth: "2008-07-22", email: "liam@example.com",   wishlistUrl: "https://amazon.com/hz/wishlist/liam",   isAdmin: false, parentId: "user-t01" },
  { id: "user-t03", userName: "olivia.w", firstName: "Olivia", dateOfBirth: "2011-11-05", email: "olivia@example.com", wishlistUrl: "",                                       isAdmin: false, parentId: "user-t01" },
  // Family B — Noah's household
  { id: "user-t04", userName: "noah.c",   firstName: "Noah",   dateOfBirth: "1982-01-30", email: "noah@example.com",   wishlistUrl: "https://amazon.com/hz/wishlist/noah",   isAdmin: false, isParent: true },
  { id: "user-t05", userName: "sophia.c", firstName: "Sophia", dateOfBirth: "2010-09-18", email: "sophia@example.com", wishlistUrl: "https://amazon.com/hz/wishlist/sophia", password: "test123", isAdmin: false, parentId: "user-t04" },
  // Family C — Mason's household
  { id: "user-t06", userName: "mason.r",  firstName: "Mason",  dateOfBirth: "1980-05-12", email: "mason@example.com",  wishlistUrl: "https://amazon.com/hz/wishlist/mason",  password: "test123", isAdmin: false, isParent: true },
  { id: "user-t07", userName: "ava.r",    firstName: "Ava",    dateOfBirth: "2009-12-25", email: "ava@example.com",    wishlistUrl: "",                                       isAdmin: false, parentId: "user-t06" },
  { id: "user-t08", userName: "ethan.r",  firstName: "Ethan",  dateOfBirth: "2013-08-08", email: "ethan@example.com",  wishlistUrl: "https://amazon.com/hz/wishlist/ethan",  password: "test123", isAdmin: false, parentId: "user-t06" },
];

const DUMMY_USER_IDS = new Set(DUMMY_USERS.map(u => u.id));

// Extract first valid http/https URL from a string (handles pasted text like "View my wishlist here. https://...")
const extractUrl = (text) => {
  if (!text) return null;
  const match = text.match(/https?:\/\/\S+/);
  return match ? match[0].replace(/[.,)>\]]+$/, "") : null;
};

// ─── Default Exclusion: Emma ⇌ Noah (requested manual exclusion) ───
const DEFAULT_EXCLUSIONS = [
  { user1: "user-t01", user2: "user-t04" },
];

// ─── Shuffle Algorithm ───
function shuffleAssignments(users, exclusions) {
  const maxAttempts = 2000;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const givers = [...users];
    const receivers = [...users];
    // Fisher-Yates on receivers
    for (let i = receivers.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [receivers[i], receivers[j]] = [receivers[j], receivers[i]];
    }
    let valid = true;
    for (let i = 0; i < givers.length; i++) {
      const g = givers[i], r = receivers[i];
      if (g.id === r.id) { valid = false; break; }
      const gFamily = g.isParent ? g.id : g.parentId;
      const rFamily = r.isParent ? r.id : r.parentId;
      if (gFamily && rFamily && gFamily === rFamily) { valid = false; break; }
      const excluded = exclusions.some(
        e => (e.user1 === g.id && e.user2 === r.id) || (e.user1 === r.id && e.user2 === g.id)
      );
      if (excluded) { valid = false; break; }
    }
    if (valid) {
      return givers.map((g, i) => ({ giverId: g.id, receiverId: receivers[i].id }));
    }
  }
  return null;
}

// ─── Styles ───
const CSS = `
@import url('${FONT_URL}');
:root {
  --emerald: #1a4d3e;
  --emerald-light: #2a7d64;
  --emerald-dark: #0e2f26;
  --gold: #d4a843;
  --gold-light: #f0d48a;
  --gold-dark: #b08a2a;
  --cream: #faf6ee;
  --cream-dark: #f0e8d8;
  --text: #1a1a1a;
  --text-muted: #6b6b6b;
  --red: #c0392b;
  --red-light: #e74c3c;
  --white: #ffffff;
  --shadow: 0 2px 12px rgba(0,0,0,0.08);
  --shadow-lg: 0 8px 32px rgba(0,0,0,0.12);
  --radius: 10px;
  --radius-lg: 16px;
}
* { margin: 0; padding: 0; box-sizing: border-box; }
body { background: var(--cream); color: var(--text); font-family: 'DM Sans', sans-serif; }
h1,h2,h3,h4 { font-family: 'Playfair Display', serif; }
input, select, textarea, button { font-family: 'DM Sans', sans-serif; }
`;

// ─── Reusable Components ───
const Input = ({ label, type = "text", value, onChange, error, ...props }) => (
  <div style={{ marginBottom: 16, flex: props.flex || "unset" }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--emerald)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>{label}</label>
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      style={{
        width: "100%", padding: "10px 14px", border: `2px solid ${error ? "var(--red)" : "#ddd"}`,
        borderRadius: "var(--radius)", fontSize: 14, outline: "none", background: "var(--white)",
        transition: "border-color .2s",
      }}
      onFocus={e => e.target.style.borderColor = "var(--gold)"}
      onBlur={e => e.target.style.borderColor = error ? "var(--red)" : "#ddd"}
      {...props}
    />
    {error && <span style={{ color: "var(--red)", fontSize: 12, marginTop: 2 }}>{error}</span>}
  </div>
);

const Btn = ({ children, variant = "primary", onClick, style = {}, disabled, small }) => {
  const base = {
    padding: small ? "6px 14px" : "10px 22px", borderRadius: "var(--radius)", fontWeight: 600,
    fontSize: small ? 12 : 14, border: "none", cursor: disabled ? "not-allowed" : "pointer",
    transition: "all .2s", display: "inline-flex", alignItems: "center", gap: 6, opacity: disabled ? 0.5 : 1,
    ...style,
  };
  const variants = {
    primary: { background: "var(--emerald)", color: "var(--white)" },
    gold: { background: "var(--gold)", color: "var(--emerald-dark)" },
    danger: { background: "var(--red)", color: "var(--white)" },
    outline: { background: "transparent", border: "2px solid var(--emerald)", color: "var(--emerald)" },
    ghost: { background: "transparent", color: "var(--emerald)" },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant] }}>{children}</button>;
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: "var(--white)", borderRadius: "var(--radius-lg)", padding: 28, boxShadow: "var(--shadow)", ...style }}>{children}</div>
);

const Badge = ({ children, color = "var(--emerald)" }) => (
  <span style={{ background: color + "18", color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{children}</span>
);

const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)" }} />
      <div onClick={e => e.stopPropagation()}
        style={{ position: "relative", background: "var(--white)", borderRadius: "var(--radius-lg)", padding: 32,
          maxWidth: wide ? 700 : 480, width: "90%", maxHeight: "85vh", overflow: "auto", boxShadow: "var(--shadow-lg)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontSize: 22, color: "var(--emerald-dark)" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--text-muted)" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ─── PAGES ───

// Registration Page
function RegisterPage({ onRegister, onGoLogin, users }) {
  const [form, setForm] = useState({ userName: "", firstName: "", dateOfBirth: "", email: "", password: "", confirmPassword: "", wishlistUrl: "", isParent: true, parentId: "" });
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const availableParents = (users || []).filter(u => !u.isAdmin && u.isParent);

  const validate = () => {
    const e = {};
    if (!form.userName.trim()) e.userName = "Required";
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.dateOfBirth) e.dateOfBirth = "Required";
    if (!form.isParent && !form.parentId) e.parentId = "Select a parent";
    if (form.email.trim() && !/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (form.password.length < 6) e.password = "Min 6 chars";
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords must match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = () => {
    if (validate()) onRegister(form);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: `linear-gradient(135deg, var(--emerald-dark) 0%, var(--emerald) 50%, var(--emerald-light) 100%)` }}>
      <Card style={{ maxWidth: 520, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🎁</div>
          <h1 style={{ fontSize: 28, color: "var(--emerald-dark)", marginBottom: 4 }}>Gift Exchange</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 14 }}>Create your account to join</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Input label="Username" value={form.userName} onChange={v => set("userName", v)} error={errors.userName} />
          <Input label="Email (optional)" type="email" value={form.email} onChange={v => set("email", v)} error={errors.email} />
          <Input label="First Name" value={form.firstName} onChange={v => set("firstName", v)} error={errors.firstName} />
          <Input label="Date of Birth" type="date" value={form.dateOfBirth} onChange={v => set("dateOfBirth", v)} error={errors.dateOfBirth} />
          <Input label="Wishlist URL" value={form.wishlistUrl} onChange={v => set("wishlistUrl", v)} placeholder="https://..." />
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 22 }}>
            <input type="checkbox" id="isParent" checked={form.isParent} onChange={e => set("isParent", e.target.checked)}
              style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--emerald)" }} />
            <label htmlFor="isParent" style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", cursor: "pointer" }}>I am a parent</label>
          </div>
          <Input label="Password" type="password" value={form.password} onChange={v => set("password", v)} error={errors.password} />
          <Input label="Confirm Password" type="password" value={form.confirmPassword} onChange={v => set("confirmPassword", v)} error={errors.confirmPassword} />
        </div>
        {!form.isParent && (
          <div style={{ marginTop: 8 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--emerald)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Parent</label>
            <select value={form.parentId} onChange={e => set("parentId", e.target.value)}
              style={{ width: "100%", padding: "10px 14px", border: `2px solid ${errors.parentId ? "var(--red)" : "#ddd"}`, borderRadius: "var(--radius)", fontSize: 14 }}>
              <option value="">Select parent...</option>
              {availableParents.map(u => <option key={u.id} value={u.id}>{u.firstName} ({u.userName})</option>)}
            </select>
            {errors.parentId && <span style={{ color: "var(--red)", fontSize: 12 }}>{errors.parentId}</span>}
          </div>
        )}
        <Btn onClick={submit} style={{ width: "100%", justifyContent: "center", marginTop: 12, padding: "12px 0" }}>Create Account</Btn>
        <p style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "var(--text-muted)" }}>
          Already have an account? <span style={{ color: "var(--gold-dark)", cursor: "pointer", fontWeight: 600 }} onClick={onGoLogin}>Sign In</span>
        </p>
      </Card>
    </div>
  );
}

// Login Page
function LoginPage({ onLogin, onGoRegister }) {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    const result = await onLogin(userName, password);
    if (result === "ratelimited") setError("Too many attempts. Try again in 15 minutes.");
    else if (result === "disabled") setError("This account has been disabled.");
    else if (!result) setError("Invalid credentials");
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: `linear-gradient(135deg, var(--emerald-dark) 0%, var(--emerald) 50%, var(--emerald-light) 100%)` }}>
      <Card style={{ maxWidth: 400, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🎁</div>
          <h1 style={{ fontSize: 28, color: "var(--emerald-dark)", marginBottom: 4 }}>Welcome Back</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 14 }}>Sign in to your gift exchange</p>
        </div>
        {error && <div style={{ background: "var(--red)" + "14", color: "var(--red)", padding: "8px 14px", borderRadius: "var(--radius)", marginBottom: 16, fontSize: 13, fontWeight: 500 }}>{error}</div>}
        <Input label="Username" value={userName} onChange={setUserName} />
        <Input label="Password" type="password" value={password} onChange={setPassword} />
        <Btn onClick={submit} style={{ width: "100%", justifyContent: "center", padding: "12px 0" }}>Sign In</Btn>
        <p style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "var(--text-muted)" }}>
          Contact the admin to create an account.
        </p>
      </Card>
    </div>
  );
}

// ─── Admin Dashboard ───
function AdminDashboard({ users, setUsers, exclusions, setExclusions, assignments, setAssignments, settings, setSettings, currentUser, onLogout, onRegister }) {
  const [tab, setTab] = useState("users");
  const [editUser, setEditUser] = useState(null);
  const [addUserModal, setAddUserModal] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ userName: "", firstName: "", dateOfBirth: "", email: "", password: "", wishlistUrl: "", isParent: true, parentId: "" });
  const [addUserErrors, setAddUserErrors] = useState({});
  const setAU = (k, v) => setAddUserForm(p => ({ ...p, [k]: v }));
  const openAddUser = () => { setAddUserForm({ userName: "", firstName: "", dateOfBirth: "", email: "", password: "", wishlistUrl: "", isParent: true, parentId: "" }); setAddUserErrors({}); setAddUserModal(true); };
  const submitAddUser = () => {
    const e = {};
    if (!addUserForm.userName.trim()) e.userName = "Required";
    else if (users.find(u => u.userName.toLowerCase() === addUserForm.userName.toLowerCase())) e.userName = "Username taken";
    if (!addUserForm.firstName.trim()) e.firstName = "Required";
    if (!addUserForm.dateOfBirth) e.dateOfBirth = "Required";
    if (!addUserForm.password || addUserForm.password.length < 6) e.password = "Min 6 chars";
    if (!addUserForm.isParent && !addUserForm.parentId) e.parentId = "Required";
    setAddUserErrors(e);
    if (Object.keys(e).length > 0) return;
    onRegister({ ...addUserForm, confirmPassword: addUserForm.password });
    setAddUserModal(false);
  };
  const [exModal, setExModal] = useState(false);
  const [exUser1, setExUser1] = useState("");
  const [exUser2, setExUser2] = useState("");
  const [shuffleMsg, setShuffleMsg] = useState("");
  const [importModal, setImportModal] = useState(false);
  const [importText, setImportText] = useState("");

  const [exportModal, setExportModal] = useState(false);
  const [exportText, setExportText] = useState("");
  const [copyMsg, setCopyMsg] = useState("");

  const nonAdminUsers = users.filter(u => !u.isAdmin && (settings.testUsersEnabled || !DUMMY_USER_IDS.has(u.id)));

  // Export
  const exportData = () => {
    const header = "UserName\tFirstName\tDateOfBirth\tEmail\tWishlistUrl\tIsParent\tParentId";
    const rows = nonAdminUsers.map(u => `${u.userName}\t${u.firstName}\t${u.dateOfBirth}\t${u.email}\t${u.wishlistUrl || ""}\t${u.isParent ? "true" : "false"}\t${u.parentId || ""}`);
    setExportText([header, ...rows].join("\n"));
    setExportModal(true);
    setCopyMsg("");
  };

  const copyExport = () => {
    const ta = document.getElementById("export-textarea");
    if (ta) {
      ta.focus();
      ta.select();
      setCopyMsg("Text selected — press Ctrl+C (or Cmd+C) to copy!");
      setTimeout(() => setCopyMsg(""), 4000);
    }
  };

  // Import
  const doImport = () => {
    const lines = importText.trim().split("\n").filter(l => l.trim());
    if (lines.length < 2) return;
    const newUsers = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split("\t");
      if (cols.length < 5) continue;
      const existing = users.find(u => u.userName.toLowerCase() === cols[0].trim().toLowerCase());
      if (existing) continue;
      newUsers.push({
        id: "user-" + Date.now() + "-" + i,
        userName: cols[0].trim(),
        firstName: cols[1].trim(),
        dateOfBirth: cols[2].trim(),
        email: cols[3].trim(),
        wishlistUrl: cols[4]?.trim() || "",
        isParent: cols[5]?.trim().toLowerCase() === "true",
        parentId: cols[6]?.trim() || undefined,
        isAdmin: false,
      });
    }
    if (newUsers.length > 0) {
      const updated = [...users, ...newUsers];
      setUsers(updated);
    }
    setImportModal(false);
    setImportText("");
  };

  // Shuffle
  const doShuffle = () => {
    const activeUsers = nonAdminUsers.filter(u => !u.disabled);
    if (activeUsers.length < 2) { setShuffleMsg("Need at least 2 active participants."); return; }
    const result = shuffleAssignments(activeUsers, exclusions);
    if (!result) { setShuffleMsg("Cannot create valid assignments with current constraints. Try removing some exclusions."); return; }
    setAssignments(result);
    saveData(STORAGE_KEYS.assignments, result);
    setShuffleMsg(`Shuffled! ${result.length} assignments created.`);
  };

  // Toggle disabled
  const toggleDisable = (id) => {
    const updated = users.map(u => u.id === id ? { ...u, disabled: !u.disabled } : u);
    setUsers(updated);
  };

  // Delete user
  const deleteUser = (id) => {
    const updated = users.filter(u => u.id !== id);
    setUsers(updated);
    setExclusions(prev => { const n = prev.filter(e => e.user1 !== id && e.user2 !== id); saveData(STORAGE_KEYS.exclusions, n); return n; });
    setAssignments(prev => { const n = prev.filter(a => a.giverId !== id && a.receiverId !== id); saveData(STORAGE_KEYS.assignments, n); return n; });
  };

  // Add exclusion
  const addExclusion = () => {
    if (!exUser1 || !exUser2 || exUser1 === exUser2) return;
    const exists = exclusions.some(e => (e.user1 === exUser1 && e.user2 === exUser2) || (e.user1 === exUser2 && e.user2 === exUser1));
    if (exists) return;
    const updated = [...exclusions, { user1: exUser1, user2: exUser2 }];
    setExclusions(updated);
    saveData(STORAGE_KEYS.exclusions, updated);
    setExModal(false);
    setExUser1(""); setExUser2("");
  };

  const removeExclusion = (idx) => {
    const updated = exclusions.filter((_, i) => i !== idx);
    setExclusions(updated);
    saveData(STORAGE_KEYS.exclusions, updated);
  };

  const getUserName = (id) => { const u = users.find(u => u.id === id); return u ? u.firstName : "Unknown"; };
  const getParentName = (u) => { if (u.isParent) return null; const p = users.find(x => x.id === u.parentId); return p ? p.firstName : null; };

  const tabs = [
    { key: "users", label: "👥 Users", count: nonAdminUsers.length },
    { key: "exclusions", label: "🚫 Exclusions", count: exclusions.length },
    { key: "shuffle", label: "🔀 Assignments", count: assignments.length },
    { key: "io", label: "📦 Import / Export" },
    { key: "settings", label: "⚙️ Settings" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream)" }}>
      {/* Header */}
      <div style={{ background: "var(--emerald-dark)", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 28 }}>🎁</span>
          <div>
            <h2 style={{ color: "var(--gold-light)", fontSize: 20, lineHeight: 1.1 }}>Gift Exchange</h2>
            <span style={{ color: "var(--emerald-light)", fontSize: 12 }}>Admin Dashboard</span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ color: "var(--cream)", fontSize: 13 }}>Hello, <strong>{currentUser.firstName}</strong></span>
          <Btn variant="gold" small onClick={onLogout}>Sign Out</Btn>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, padding: "20px 28px 0", borderBottom: "2px solid var(--cream-dark)", flexWrap: "wrap" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setShuffleMsg(""); }}
            style={{
              padding: "10px 20px", border: "none", borderBottom: tab === t.key ? "3px solid var(--gold)" : "3px solid transparent",
              background: tab === t.key ? "var(--white)" : "transparent", borderRadius: "var(--radius) var(--radius) 0 0",
              fontWeight: 600, fontSize: 13, cursor: "pointer", color: tab === t.key ? "var(--emerald-dark)" : "var(--text-muted)",
              display: "flex", alignItems: "center", gap: 6, transition: "all .2s",
            }}>
            {t.label}
            {t.count !== undefined && <span style={{ background: tab === t.key ? "var(--gold)" : "#ddd", color: tab === t.key ? "var(--emerald-dark)" : "var(--text-muted)", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{t.count}</span>}
          </button>
        ))}
      </div>

      <div style={{ padding: 28, maxWidth: 1100, margin: "0 auto" }}>

        {/* ── Users Tab ── */}
        {tab === "users" && (
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ fontSize: 20, color: "var(--emerald-dark)" }}>Participants</h3>
              <Btn variant="primary" onClick={openAddUser}>+ Add User</Btn>
            </div>
            {nonAdminUsers.length === 0 ? (
              <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 40 }}>No participants yet. Use "Add User" or import a file.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--cream-dark)", textAlign: "left" }}>
                      {["Username", "Name", "Parent", "DOB", "Email", "Wishlist", "Actions"].map(h => (
                        <th key={h} style={{ padding: "10px 12px", fontWeight: 600, color: "var(--emerald)", textTransform: "uppercase", fontSize: 11, letterSpacing: .8 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {nonAdminUsers.map(u => (
                      <tr key={u.id} style={{ borderBottom: "1px solid var(--cream-dark)", opacity: u.disabled ? 0.5 : 1 }}>
                        <td style={{ padding: "10px 12px", fontWeight: 600 }}>
                          {u.userName}
                          {u.disabled && <span style={{ marginLeft: 6, fontSize: 10, background: "var(--red)", color: "#fff", borderRadius: 4, padding: "1px 5px", fontWeight: 700, verticalAlign: "middle" }}>DISABLED</span>}
                        </td>
                        <td style={{ padding: "10px 12px" }}>{u.firstName}</td>
                        <td style={{ padding: "10px 12px" }}>{u.isParent ? <Badge color="var(--gold-dark)">Parent</Badge> : getParentName(u) ? <Badge>{getParentName(u)}</Badge> : "—"}</td>
                        <td style={{ padding: "10px 12px" }}>{u.dateOfBirth}</td>
                        <td style={{ padding: "10px 12px" }}>{u.email}</td>
                        <td style={{ padding: "10px 12px" }}>{extractUrl(u.wishlistUrl) ? <a href={extractUrl(u.wishlistUrl)} target="_blank" rel="noreferrer" style={{ color: "var(--gold-dark)" }}>View</a> : "—"}</td>
                        <td style={{ padding: "10px 12px", display: "flex", gap: 4 }}>
                          <Btn small variant="outline" onClick={() => setEditUser({ ...u, newPassword: "" })}>Edit</Btn>
                          <Btn small variant={u.disabled ? "primary" : "outline"} onClick={() => toggleDisable(u.id)}>{u.disabled ? "Enable" : "Disable"}</Btn>
                          <Btn small variant="danger" onClick={() => deleteUser(u.id)}>Delete</Btn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* ── Exclusions Tab ── */}
        {tab === "exclusions" && (
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h3 style={{ fontSize: 20, color: "var(--emerald-dark)" }}>User Exclusions</h3>
                <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>Pairs that will not be assigned to each other</p>
              </div>
              <Btn variant="gold" onClick={() => setExModal(true)}>+ Add Exclusion</Btn>
            </div>
            {exclusions.length === 0 ? (
              <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 40 }}>No exclusions. Family matches blocked automatically.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {exclusions.map((ex, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "var(--cream)", borderRadius: "var(--radius)", border: "1px solid var(--cream-dark)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14 }}>
                      <strong>{getUserName(ex.user1)}</strong>
                      <span style={{ color: "var(--red)", fontSize: 18 }}>⇌</span>
                      <strong>{getUserName(ex.user2)}</strong>
                    </div>
                    <Btn small variant="danger" onClick={() => removeExclusion(i)}>Remove</Btn>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* ── Shuffle Tab ── */}
        {tab === "shuffle" && (
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <h3 style={{ fontSize: 20, color: "var(--emerald-dark)" }}>Assignments</h3>
                <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{nonAdminUsers.length} participants · {exclusions.length} exclusions · Family blocking active</p>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="gold" onClick={doShuffle}>🔀 {assignments.length > 0 ? "Reshuffle" : "Shuffle"}</Btn>
                {assignments.length > 0 && <Btn variant="danger" onClick={() => { setAssignments([]); saveData(STORAGE_KEYS.assignments, []); setShuffleMsg("Assignments cleared."); }}>Clear</Btn>}
              </div>
            </div>
            {shuffleMsg && <div style={{ padding: "10px 16px", borderRadius: "var(--radius)", marginBottom: 16, background: shuffleMsg.includes("Cannot") ? "var(--red)" + "14" : "var(--emerald)" + "14", color: shuffleMsg.includes("Cannot") ? "var(--red)" : "var(--emerald)", fontSize: 13, fontWeight: 500 }}>{shuffleMsg}</div>}
            {assignments.length === 0 ? (
              <p style={{ color: "var(--text-muted)", textAlign: "center", padding: 40 }}>No assignments yet. Click Shuffle to generate.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                {assignments.map((a, i) => {
                  const giver = users.find(u => u.id === a.giverId);
                  const receiver = users.find(u => u.id === a.receiverId);
                  if (!giver || !receiver) return null;
                  return (
                    <div key={i} style={{ padding: 16, borderRadius: "var(--radius)", border: "2px solid var(--cream-dark)", background: "var(--cream)", display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 38, height: 38, borderRadius: "50%", background: "var(--emerald)", color: "var(--gold-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                        {giver.firstName[0]}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{giver.firstName}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>gives to</div>
                      </div>
                      <span style={{ fontSize: 18, color: "var(--gold-dark)" }}>→</span>
                      <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{receiver.firstName}</div>
                        {extractUrl(receiver.wishlistUrl) && <a href={extractUrl(receiver.wishlistUrl)} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--gold-dark)" }}>wishlist</a>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}

        {/* ── Import/Export Tab ── */}
        {tab === "io" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <Card>
              <h3 style={{ fontSize: 20, color: "var(--emerald-dark)", marginBottom: 8 }}>Export</h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>Download all participant data as tab-separated text file.</p>
              <Btn variant="gold" onClick={exportData} disabled={nonAdminUsers.length === 0}>📥 Export Participants</Btn>
            </Card>
            <Card>
              <h3 style={{ fontSize: 20, color: "var(--emerald-dark)", marginBottom: 8 }}>Import</h3>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>Paste tab-separated data. Existing usernames are skipped. Admin must set passwords for imported users.</p>
              <Btn variant="primary" onClick={() => setImportModal(true)}>📤 Import Participants</Btn>
            </Card>
          </div>
        )}
        {/* ── Settings Tab ── */}
        {tab === "settings" && (
          <Card>
            <h3 style={{ fontSize: 20, color: "var(--emerald-dark)", marginBottom: 20 }}>Settings</h3>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "var(--cream)", borderRadius: "var(--radius)", border: "1px solid var(--cream-dark)", marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>Gift Limit ($)</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Spending limit shown on every assignment card</div>
              </div>
              <input type="number" min="0" value={settings.giftLimit ?? 50}
                onChange={e => setSettings({ ...settings, giftLimit: Number(e.target.value) })}
                style={{ width: 80, padding: "6px 10px", border: "2px solid #ddd", borderRadius: "var(--radius)", fontSize: 14, textAlign: "right" }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", background: "var(--cream)", borderRadius: "var(--radius)", border: "1px solid var(--cream-dark)" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>Test Users</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Show dummy seed users in all views and assignments</div>
              </div>
              <button
                onClick={() => setSettings({ ...settings, testUsersEnabled: !settings.testUsersEnabled })}
                style={{
                  width: 48, height: 26, borderRadius: 13, border: "none", cursor: "pointer", transition: "background .2s",
                  background: settings.testUsersEnabled ? "var(--emerald-light)" : "#ccc", position: "relative", flexShrink: 0,
                }}
              >
                <span style={{
                  position: "absolute", top: 3, left: settings.testUsersEnabled ? 25 : 3,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left .2s", display: "block",
                }} />
              </button>
            </div>
          </Card>
        )}

      </div>

      {/* ── Edit User Modal ── */}
      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Edit User">
        {editUser && (
          <>
            <Input label="Username" value={editUser.userName} onChange={v => setEditUser(p => ({ ...p, userName: v }))} />
            <Input label="First Name" value={editUser.firstName} onChange={v => setEditUser(p => ({ ...p, firstName: v }))} />
            <Input label="Date of Birth" type="date" value={editUser.dateOfBirth} onChange={v => setEditUser(p => ({ ...p, dateOfBirth: v }))} />
            <Input label="Email" value={editUser.email} onChange={v => setEditUser(p => ({ ...p, email: v }))} />
            <Input label="Wishlist URL" value={editUser.wishlistUrl} onChange={v => setEditUser(p => ({ ...p, wishlistUrl: v }))} />
            <Input label="New Password (leave blank to keep)" type="password" value={editUser.newPassword || ""} onChange={v => setEditUser(p => ({ ...p, newPassword: v }))} placeholder="Unchanged" />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <input type="checkbox" id="editIsParent" checked={!!editUser.isParent} onChange={e => setEditUser(p => ({ ...p, isParent: e.target.checked, parentId: e.target.checked ? undefined : p.parentId }))}
                style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--emerald)" }} />
              <label htmlFor="editIsParent" style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", cursor: "pointer" }}>Parent</label>
            </div>
            {!editUser.isParent && (
              <div style={{ marginTop: 8 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--emerald)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Parent</label>
                <select value={editUser.parentId || ""} onChange={e => setEditUser(p => ({ ...p, parentId: e.target.value }))}
                  style={{ width: "100%", padding: "10px 14px", border: "2px solid #ddd", borderRadius: "var(--radius)", fontSize: 14 }}>
                  <option value="">None</option>
                  {nonAdminUsers.filter(u => u.isParent && u.id !== editUser.id).map(u => <option key={u.id} value={u.id}>{u.firstName} ({u.userName})</option>)}
                </select>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <Btn variant="outline" onClick={() => setEditUser(null)}>Cancel</Btn>
              <Btn onClick={async () => {
                if (editUser.newPassword) {
                  if (editUser.newPassword.length < 6) { alert("Password must be at least 6 characters."); return; }
                  const r = await fetch(window.location.origin + "/api/auth/admin-reset-password", {
                    method: "POST", headers: authHeaders(),
                    body: JSON.stringify({ userId: editUser.id, newPassword: editUser.newPassword }),
                  });
                  if (!r.ok) { alert("Password reset failed."); return; }
                }
                const { newPassword: _pw, ...rest } = editUser;
                const updated = users.map(u => u.id === rest.id ? { ...u, ...rest } : u);
                setUsers(updated);
                setEditUser(null);
              }}>Save Changes</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* ── Exclusion Modal ── */}
      <Modal open={exModal} onClose={() => setExModal(false)} title="Add Exclusion">
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>Select two participants who should not be assigned to each other.</p>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--emerald)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>User 1</label>
          <select value={exUser1} onChange={e => setExUser1(e.target.value)} style={{ width: "100%", padding: "10px 14px", border: "2px solid #ddd", borderRadius: "var(--radius)", fontSize: 14 }}>
            <option value="">Select user...</option>
            {nonAdminUsers.map(u => <option key={u.id} value={u.id}>{u.firstName} ({u.userName})</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--emerald)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>User 2</label>
          <select value={exUser2} onChange={e => setExUser2(e.target.value)} style={{ width: "100%", padding: "10px 14px", border: "2px solid #ddd", borderRadius: "var(--radius)", fontSize: 14 }}>
            <option value="">Select user...</option>
            {nonAdminUsers.filter(u => u.id !== exUser1).map(u => <option key={u.id} value={u.id}>{u.firstName} ({u.userName})</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="outline" onClick={() => setExModal(false)}>Cancel</Btn>
          <Btn onClick={addExclusion} disabled={!exUser1 || !exUser2}>Add Exclusion</Btn>
        </div>
      </Modal>

      {/* ── Import Modal ── */}
      <Modal open={importModal} onClose={() => setImportModal(false)} title="Import Participants" wide>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>Paste tab-separated data. First row = header. Columns: UserName, FirstName, DateOfBirth, Email, WishlistUrl, IsParent, ParentId</p>
        <textarea value={importText} onChange={e => setImportText(e.target.value)}
          rows={10} placeholder={`UserName\tFirstName\tDateOfBirth\tEmail\tWishlistUrl\tIsParent\tParentId\njdoe\tJohn\t1990-05-15\tjohn@example.com\thttps://wishlist.com/john\ttrue\t`}
          style={{ width: "100%", padding: 12, border: "2px solid #ddd", borderRadius: "var(--radius)", fontSize: 13, fontFamily: "monospace", resize: "vertical" }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <Btn variant="outline" onClick={() => setImportModal(false)}>Cancel</Btn>
          <Btn onClick={doImport}>Import</Btn>
        </div>
      </Modal>

      {/* ── Export Modal ── */}
      <Modal open={exportModal} onClose={() => setExportModal(false)} title="Export Participants" wide>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>Tab-separated participant data. Copy and paste into a text file.</p>
        <textarea id="export-textarea" readOnly value={exportText}
          rows={12}
          style={{ width: "100%", padding: 12, border: "2px solid #ddd", borderRadius: "var(--radius)", fontSize: 13, fontFamily: "monospace", resize: "vertical", background: "var(--cream)" }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", marginTop: 12 }}>
          {copyMsg && <span style={{ color: "var(--emerald)", fontWeight: 600, fontSize: 13 }}>✓ {copyMsg}</span>}
          <Btn variant="outline" onClick={() => setExportModal(false)}>Close</Btn>
          <Btn variant="gold" onClick={copyExport}>📋 Select All</Btn>
        </div>
      </Modal>

      {/* ── Add User Modal ── */}
      <Modal open={addUserModal} onClose={() => setAddUserModal(false)} title="Add User" wide>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <Input label="Username" value={addUserForm.userName} onChange={v => setAU("userName", v)} error={addUserErrors.userName} />
          <Input label="First Name" value={addUserForm.firstName} onChange={v => setAU("firstName", v)} error={addUserErrors.firstName} />
          <Input label="Date of Birth" type="date" value={addUserForm.dateOfBirth} onChange={v => setAU("dateOfBirth", v)} error={addUserErrors.dateOfBirth} />
          <Input label="Initial Password" type="password" value={addUserForm.password} onChange={v => setAU("password", v)} error={addUserErrors.password} />
          <Input label="Email (optional)" type="email" value={addUserForm.email} onChange={v => setAU("email", v)} />
          <Input label="Wishlist URL (optional)" value={addUserForm.wishlistUrl} onChange={v => setAU("wishlistUrl", v)} placeholder="https://..." />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
          <input type="checkbox" id="auIsParent" checked={addUserForm.isParent} onChange={e => setAU("isParent", e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--emerald)" }} />
          <label htmlFor="auIsParent" style={{ fontSize: 14, fontWeight: 600, color: "var(--text)", cursor: "pointer" }}>Parent</label>
        </div>
        {!addUserForm.isParent && (
          <div style={{ marginTop: 8 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--emerald)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Parent</label>
            <select value={addUserForm.parentId} onChange={e => setAU("parentId", e.target.value)}
              style={{ width: "100%", padding: "10px 14px", border: `2px solid ${addUserErrors.parentId ? "var(--red)" : "#ddd"}`, borderRadius: "var(--radius)", fontSize: 14 }}>
              <option value="">Select parent...</option>
              {nonAdminUsers.filter(u => u.isParent).map(u => <option key={u.id} value={u.id}>{u.firstName} ({u.userName})</option>)}
            </select>
            {addUserErrors.parentId && <span style={{ color: "var(--red)", fontSize: 12 }}>{addUserErrors.parentId}</span>}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <Btn variant="outline" onClick={() => setAddUserModal(false)}>Cancel</Btn>
          <Btn onClick={submitAddUser}>Create User</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── User Dashboard (non-admin) ───
function UserDashboard({ currentUser, assignments, users, settings, onLogout, onUpdateUser }) {
  const myAssignment = assignments.find(a => a.giverId === currentUser.id);
  const receiver = myAssignment ? users.find(u => u.id === myAssignment.receiverId) : null;

  const [editModal, setEditModal] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [editErrors, setEditErrors] = useState({});
  const [pwErrors, setPwErrors] = useState({});
  const [toast, setToast] = useState("");
  const [showDirectory, setShowDirectory] = useState(false);

  const nonAdminUsers = users.filter(u => !u.isAdmin && !u.disabled && (settings.testUsersEnabled || !DUMMY_USER_IDS.has(u.id)));

  const openEdit = () => {
    setEditForm({ firstName: currentUser.firstName, email: currentUser.email, dateOfBirth: currentUser.dateOfBirth, wishlistUrl: currentUser.wishlistUrl || "" });
    setEditErrors({});
    setEditModal(true);
  };

  const saveProfile = async () => {
    const e = {};
    if (!editForm.firstName.trim()) e.firstName = "Required";
    if (editForm.email.trim() && !/\S+@\S+\.\S+/.test(editForm.email)) e.email = "Enter a valid email";
    if (!editForm.dateOfBirth) e.dateOfBirth = "Required";
    setEditErrors(e);
    if (Object.keys(e).length > 0) return;
    try {
      const r = await fetch(UPDATE_PROFILE_ENDPOINT, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(editForm),
      });
      const data = await r.json();
      if (!r.ok) { setEditErrors({ firstName: data.error || "Save failed" }); return; }
      onUpdateUser(data.user);
    } catch { setEditErrors({ firstName: "Network error" }); return; }
    setEditModal(false);
    setToast("Profile updated!");
    setTimeout(() => setToast(""), 3000);
  };

  const openPw = () => { setPwForm({ current: "", newPw: "", confirm: "" }); setPwErrors({}); setPwModal(true); };

  const savePw = async () => {
    const e = {};
    if (pwForm.newPw.length < 6) e.newPw = "Min 6 chars";
    if (pwForm.newPw !== pwForm.confirm) e.confirm = "Passwords must match";
    setPwErrors(e);
    if (Object.keys(e).length > 0) return;
    try {
      const r = await fetch(CHANGE_PW_ENDPOINT, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.newPw }),
      });
      const data = await r.json();
      if (!r.ok) { setPwErrors({ current: data.error || "Failed" }); return; }
    } catch { setPwErrors({ current: "Network error" }); return; }
    setPwModal(false);
    setToast("Password changed!");
    setTimeout(() => setToast(""), 3000);
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--cream)" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "var(--emerald-dark)", color: "var(--gold-light)", padding: "10px 24px", borderRadius: "var(--radius)", boxShadow: "var(--shadow-lg)", fontSize: 14, fontWeight: 500 }}>
          {toast}
        </div>
      )}
      <div style={{ background: "var(--emerald-dark)", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 28 }}>🎁</span>
          <h2 style={{ color: "var(--gold-light)", fontSize: 20 }}>Gift Exchange</h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ color: "var(--cream)", fontSize: 13 }}>Hello, <strong>{currentUser.firstName}</strong></span>
          <Btn variant="gold" small onClick={onLogout}>Sign Out</Btn>
        </div>
      </div>
      <div style={{ padding: 28, maxWidth: 600, margin: "0 auto" }}>
        <Card style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎄</div>
          <h2 style={{ fontSize: 24, color: "var(--emerald-dark)", marginBottom: 8 }}>Your Assignment</h2>
          {receiver ? (
            <div style={{ padding: 20, background: "var(--cream)", borderRadius: "var(--radius-lg)", marginTop: 16 }}>
              <p style={{ fontSize: 14, color: "var(--text-muted)", marginBottom: 4 }}>You are giving a gift to:</p>
              {settings.giftLimit > 0 && <p style={{ fontSize: 12, fontWeight: 700, color: "var(--emerald)", marginBottom: 8 }}>Up to ${settings.giftLimit}</p>}
              <h3 style={{ fontSize: 28, color: "var(--emerald-dark)" }}>{receiver.firstName}</h3>
              {extractUrl(receiver.wishlistUrl) ? (
                <div style={{ marginTop: 16 }}>
                  <a href={extractUrl(receiver.wishlistUrl)} target="_blank" rel="noopener noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", background: "var(--gold)", color: "var(--emerald-dark)", borderRadius: "var(--radius)", fontWeight: 700, fontSize: 15, textDecoration: "none", transition: "opacity .2s" }}
                    onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                    🎁 View Their Wishlist
                  </a>
                  <p style={{ marginTop: 8, fontSize: 12, color: "var(--text-muted)", wordBreak: "break-all" }}>{receiver.wishlistUrl}</p>
                </div>
              ) : (
                <p style={{ marginTop: 12, fontSize: 13, color: "var(--text-muted)", fontStyle: "italic" }}>No wishlist provided — ask them what they'd like!</p>
              )}
            </div>
          ) : (
            <p style={{ color: "var(--text-muted)", padding: 20 }}>Assignments haven't been made yet. Check back soon!</p>
          )}
        </Card>
        <Card style={{ marginTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ fontSize: 18, color: "var(--emerald-dark)" }}>Your Profile</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn small variant="outline" onClick={openEdit}>✏️ Edit</Btn>
              <Btn small variant="ghost" onClick={openPw}>🔒 Password</Btn>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px", fontSize: 13 }}>
            <div><span style={{ color: "var(--text-muted)" }}>Username:</span> <strong>{currentUser.userName}</strong></div>
            <div><span style={{ color: "var(--text-muted)" }}>Email:</span> <strong>{currentUser.email}</strong></div>
            <div><span style={{ color: "var(--text-muted)" }}>Role:</span> <Badge color={currentUser.isParent ? "var(--gold-dark)" : "var(--emerald)"}>{currentUser.isParent ? "Parent" : "Child"}</Badge></div>
            <div><span style={{ color: "var(--text-muted)" }}>DOB:</span> <strong>{currentUser.dateOfBirth}</strong></div>
            <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "var(--text-muted)" }}>Wishlist:</span> {extractUrl(currentUser.wishlistUrl) ? <a href={extractUrl(currentUser.wishlistUrl)} target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold-dark)", fontWeight: 600, wordBreak: "break-all" }}>{extractUrl(currentUser.wishlistUrl)}</a> : <em style={{ color: "var(--text-muted)" }}>Not set</em>}</div>
          </div>
        </Card>

        {/* Participants Directory Link Card */}
        <Card style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h3 style={{ fontSize: 18, color: "var(--emerald-dark)", marginBottom: 4 }}>👥 Participants</h3>
            <p style={{ fontSize: 13, color: "var(--text-muted)" }}>View everyone in the gift exchange</p>
          </div>
          <Btn variant="outline" onClick={() => setShowDirectory(true)}>View Directory →</Btn>
        </Card>
      </div>

      {/* Participants Directory Page (overlay) */}
      {showDirectory && (
        <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "var(--cream)", overflow: "auto" }}>
          <div style={{ background: "var(--emerald-dark)", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 28 }}>👥</span>
              <h2 style={{ color: "var(--gold-light)", fontSize: 20 }}>Participants Directory</h2>
            </div>
            <Btn variant="gold" small onClick={() => setShowDirectory(false)}>← Back</Btn>
          </div>
          <div style={{ padding: 28, maxWidth: 700, margin: "0 auto" }}>
            <Card>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>{nonAdminUsers.length} participant{nonAdminUsers.length !== 1 ? "s" : ""} in the exchange</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {nonAdminUsers
                  .sort((a, b) => a.firstName.localeCompare(b.firstName))
                  .map(u => {
                    const bday = u.dateOfBirth ? new Date(u.dateOfBirth + "T00:00:00") : null;
                    const bdayStr = bday ? bday.toLocaleDateString("en-US", { month: "long", day: "numeric" }) : "—";
                    return (
                      <div key={u.id} style={{ display: "flex", alignItems: "center", padding: "12px 16px", background: "var(--cream)", borderRadius: "var(--radius)", border: "1px solid var(--cream-dark)", gap: 14 }}>
                        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--emerald)", color: "var(--gold-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                          {u.firstName[0]}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>{u.firstName} {u.isParent && <Badge color="var(--gold-dark)">Parent</Badge>}</div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>🎂 {bdayStr}</div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          {extractUrl(u.wishlistUrl) ? (
                            <a href={extractUrl(u.wishlistUrl)} target="_blank" rel="noopener noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", background: "var(--gold)", color: "var(--emerald-dark)", borderRadius: "var(--radius)", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>
                              🎁 Wishlist
                            </a>
                          ) : (
                            <span style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>No wishlist</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      <Modal open={editModal} onClose={() => setEditModal(false)} title="Edit Profile">
        <Input label="First Name" value={editForm.firstName || ""} onChange={v => setEditForm(p => ({ ...p, firstName: v }))} error={editErrors.firstName} />
        <Input label="Email (optional)" type="email" value={editForm.email || ""} onChange={v => setEditForm(p => ({ ...p, email: v }))} error={editErrors.email} />
        <Input label="Date of Birth" type="date" value={editForm.dateOfBirth || ""} onChange={v => setEditForm(p => ({ ...p, dateOfBirth: v }))} error={editErrors.dateOfBirth} />
        <Input label="Wishlist URL" value={editForm.wishlistUrl || ""} onChange={v => setEditForm(p => ({ ...p, wishlistUrl: v }))} placeholder="https://..." />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <Btn variant="outline" onClick={() => setEditModal(false)}>Cancel</Btn>
          <Btn onClick={saveProfile}>Save Changes</Btn>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal open={pwModal} onClose={() => setPwModal(false)} title="Change Password">
        <Input label="Current Password" type="password" value={pwForm.current} onChange={v => setPwForm(p => ({ ...p, current: v }))} error={pwErrors.current} />
        <Input label="New Password" type="password" value={pwForm.newPw} onChange={v => setPwForm(p => ({ ...p, newPw: v }))} error={pwErrors.newPw} />
        <Input label="Confirm New Password" type="password" value={pwForm.confirm} onChange={v => setPwForm(p => ({ ...p, confirm: v }))} error={pwErrors.confirm} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <Btn variant="outline" onClick={() => setPwModal(false)}>Cancel</Btn>
          <Btn onClick={savePw}>Change Password</Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── Main App ───
export default function App() {
  const [users, setUsersRaw] = useState([DEFAULT_ADMIN, ...DUMMY_USERS]);
  const [exclusions, setExclusionsRaw] = useState(DEFAULT_EXCLUSIONS);
  const [assignments, setAssignmentsRaw] = useState([]);
  const [settings, setSettingsRaw] = useState(DEFAULT_SETTINGS);
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage] = useState("login");
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState("");

  // Wrap setters to auto-persist
  const setUsers = useCallback((val) => {
    setUsersRaw(val);
    // persisted via effect
  }, []);
  const setExclusions = useCallback((val) => { setExclusionsRaw(val); }, []);
  const setAssignments = useCallback((val) => { setAssignmentsRaw(val); }, []);
  const setSettings = useCallback((val) => { setSettingsRaw(val); }, []);

  // Persist on change
  useEffect(() => { if (loaded && currentUser?.isAdmin) saveData(STORAGE_KEYS.users, users); }, [users, loaded, currentUser]);
  useEffect(() => { if (loaded) saveData(STORAGE_KEYS.exclusions, exclusions); }, [exclusions, loaded]);
  useEffect(() => { if (loaded) saveData(STORAGE_KEYS.assignments, assignments); }, [assignments, loaded]);
  useEffect(() => { if (loaded) saveData(STORAGE_KEYS.settings, settings); }, [settings, loaded]);

  // Load on mount
  useEffect(() => {
    (async () => {
      const defaultUsers = [DEFAULT_ADMIN, ...DUMMY_USERS];
      const u = await loadData(STORAGE_KEYS.users, defaultUsers);
      // Ensure admin exists
      if (!u.find(x => x.id === DEFAULT_ADMIN.id)) u.unshift(DEFAULT_ADMIN);
      // Seed dummy users only on first load (empty storage); don't re-add deleted ones
      setUsersRaw(u);
      const ex = await loadData(STORAGE_KEYS.exclusions, DEFAULT_EXCLUSIONS);
      setExclusionsRaw(ex);
      setAssignmentsRaw(await loadData(STORAGE_KEYS.assignments, []));
      setSettingsRaw(await loadData(STORAGE_KEYS.settings, DEFAULT_SETTINGS));
      // Restore session from JWT
      const savedToken = sessionStorage.getItem("gx-token");
      if (savedToken) {
        try {
          const payload = JSON.parse(atob(savedToken.split(".")[1]));
          if (payload.exp > Date.now() / 1000) {
            const restored = u.find(x => x.id === payload.sub);
            if (restored && !restored.disabled) setCurrentUser(restored);
            else clearToken();
          } else clearToken();
        } catch { clearToken(); }
      }
      setLoaded(true);
    })();
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const handleRegister = (form) => {
    if (users.find(u => u.userName.toLowerCase() === form.userName.toLowerCase())) {
      showToast("Username already taken!");
      return;
    }
    const newUser = { ...form, id: "user-" + Date.now(), isAdmin: false };
    delete newUser.confirmPassword;
    setUsers([...users, newUser]);
    showToast("Account created! Please sign in.");
    setPage("login");
  };

  const handleLogin = async (userName, password) => {
    try {
      const r = await fetch(AUTH_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userName, password }),
      });
      if (r.status === 429) return "ratelimited";
      const data = await r.json();
      if (!r.ok) {
        if (data.error && data.error.toLowerCase().includes("disabled")) return "disabled";
        return false;
      }
      setToken(data.token);
      setCurrentUser(data.user);
      // Reload users with token so state reflects server data
      const freshUsers = await loadData(STORAGE_KEYS.users, users);
      setUsersRaw(freshUsers);
      return true;
    } catch { return false; }
  };

  const handleLogout = () => { clearToken(); setCurrentUser(null); setPage("login"); };

  const handleUpdateUser = (updatedUser) => {
    setUsersRaw(users.map(u => u.id === updatedUser.id ? updatedUser : u));
    setCurrentUser(updatedUser);
  };

  if (!loaded) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "var(--cream)" }}><h2 style={{ fontFamily: "Playfair Display, serif", color: "var(--emerald)" }}>Loading...</h2></div>;

  return (
    <>
      <style>{CSS}</style>
      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "var(--emerald-dark)", color: "var(--gold-light)", padding: "10px 24px", borderRadius: "var(--radius)", boxShadow: "var(--shadow-lg)", fontSize: 14, fontWeight: 500, animation: "fadeIn .3s" }}>
          {toast}
        </div>
      )}
      {currentUser ? (
        currentUser.isAdmin ? (
          <AdminDashboard users={users} setUsers={setUsers} exclusions={exclusions} setExclusions={setExclusions} assignments={assignments} setAssignments={setAssignments} settings={settings} setSettings={setSettings} currentUser={currentUser} onLogout={handleLogout} onRegister={handleRegister} />
        ) : (
          <UserDashboard currentUser={currentUser} assignments={assignments} users={users} settings={settings} onLogout={handleLogout} onUpdateUser={handleUpdateUser} />
        )
      ) : (
        <LoginPage onLogin={handleLogin} />
      )}
    </>
  );
}
